import * as DocumentPicker from 'expo-document-picker';

import { extractDocxText, isDocxLike } from '@/lib/docx-text';
import { isImageMime, readUriAsBase64 } from '@/lib/files';
import {
  parseReceiptText,
  scanPdfBase64,
  scanReceiptBase64,
  type ParsedReceipt,
} from '@/lib/ocr';

/**
 * Unified "pick a file and OCR it" intake (Wave 54) for the in-form
 * scan buttons on the expense / quote / inventory forms. Accepts what
 * the Smart Scan page accepts: images (Vision image OCR), PDFs (Vision
 * files:annotate), and any other document (attached without a parse —
 * the user keeps typing, the file still saves with the record).
 */
export type ScanIntakeResult =
  | { status: 'canceled' }
  | { status: 'error'; message: string }
  | {
      status: 'picked';
      uri: string;
      mime: string | null;
      name: string;
      /** null when the file type has no OCR path (docx etc.) or OCR failed. */
      parsed: ParsedReceipt | null;
    };

const MAX_OCR_BYTES = 10 * 1024 * 1024;

export async function pickAndScanFile(): Promise<ScanIntakeResult> {
  let res: DocumentPicker.DocumentPickerResult;
  try {
    res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true });
  } catch (err) {
    return { status: 'error', message: String(err) };
  }
  if (res.canceled || !res.assets[0]) return { status: 'canceled' };
  const file = res.assets[0];
  const name = file.name || 'Document';
  const mime =
    file.mimeType ?? (name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : null);
  const isPdf = mime?.includes('pdf') ?? false;
  const isImage = isImageMime(mime) || /\.(jpe?g|png|webp|heic|gif|avif|tiff?|bmp)$/i.test(name);

  let parsed: ParsedReceipt | null = null;
  if ((isPdf || isImage) && (file.size ?? 0) <= MAX_OCR_BYTES) {
    const base64 = await readUriAsBase64(file.uri);
    if (base64) {
      const result = isPdf ? await scanPdfBase64(base64) : await scanReceiptBase64(base64);
      if (result.ok) parsed = result.parsed;
    }
  } else if (isDocxLike(mime, name) && (file.size ?? 0) <= MAX_OCR_BYTES) {
    // Word docs never touch Vision — the text is extracted locally
    // from the docx zip (Wave 56), free and offline.
    const base64 = await readUriAsBase64(file.uri);
    const text = base64 ? extractDocxText(base64) : null;
    if (text) parsed = parseReceiptText(text);
  }
  return { status: 'picked', uri: file.uri, mime, name, parsed };
}
